// @givmo/fundraisers-module — requests to join a campaign (FCM-WEB33; backend FCM-BE47, D-FCM-27): the
// reads the Campaign and Layers tabs make, each a GENERATED op over the owner-agnostic
// `/fundraisers/{id}/campaign…` family. The people's half is the umbrella engine's
// (CHARITY_P2P_ENABLED); the campaigns' half and the own request ride CAMPAIGN_LAYERS_ENABLED, so while
// that is off they answer the absent-route 404, which the tab reads as "not served", never an error.
import type { QueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  extractList,
  sdk,
  type P2pJoinRequestOut,
  type P2pLayerJoinPreviewOut,
  type P2pLayerRequestOut,
  type P2pOwnLayerRequestOut,
} from '@givmo/api-client';
import {
  campaignsGetOwnCampaignLayerRequestQueryKey,
  campaignsListCampaignJoinRequestsQueryKey,
  campaignsListCampaignLayerRequestsQueryKey,
  campaignsPreviewCampaignJoinParentQueryKey,
  orgFundraisersListOrgFundraisersQueryKey,
} from '@givmo/api-client/query';

/** People's requests: pages of the server's most (`le=100`), those waiting first. */
export const REQUEST_PAGE = 100;
/** Campaigns' requests: pages of at most 25, as the layer-invite lists (`LAYER_INVITE_PAGE_LIMIT`). */
export const CAMPAIGN_REQUEST_PAGE = 25;
/** The most pages one read takes: 1,000 people's or 250 campaigns' requests waiting. */
const MAX_REQUEST_PAGES = 10;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Every waiting request, page after page: the server lists those waiting first, so the read goes on while
 * a page comes back full and its last row still waits, to `MAX_REQUEST_PAGES`. Answered rows past the
 * page where the waiting ones end are not read.
 */
async function readWaiting<T extends { id: string }>(
  pageSize: number,
  readPage: (offset: number) => Promise<unknown[]>,
  isRow: (value: unknown) => value is T,
): Promise<T[]> {
  const rows: T[] = [];
  const seen = new Set<string>();
  for (let page = 0; page < MAX_REQUEST_PAGES; page += 1) {
    const batch = await readPage(page * pageSize);
    for (const value of batch) {
      if (isRow(value) && !seen.has(value.id)) {
        seen.add(value.id);
        rows.push(value);
      }
    }
    const last = batch.at(-1);
    if (batch.length < pageSize || !isRecord(last) || last.status !== 'pending') break;
  }
  return rows;
}

function isPersonRequest(value: unknown): value is P2pJoinRequestOut {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.display_name === 'string' &&
    typeof value.status === 'string' &&
    typeof value.created_at === 'string' &&
    typeof value.expires_at === 'string'
  );
}

function isCampaignRequest(value: unknown): value is P2pLayerRequestOut {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.campaign_id === 'string' &&
    typeof value.status === 'string' &&
    typeof value.created_at === 'string' &&
    typeof value.expires_at === 'string' &&
    isRecord(value.recipients) &&
    isRecord(value.layers)
  );
}

/** The people asking to start a page under this campaign, those waiting first (the read rank). */
export function peopleRequestsOptions(
  campaignId: string,
): UseQueryOptions<P2pJoinRequestOut[], Error> {
  const path = { fundraiser_id: campaignId };
  const query = (offset: number) => ({ 'page[limit]': REQUEST_PAGE, 'page[offset]': offset });
  return {
    queryKey: campaignsListCampaignJoinRequestsQueryKey({ path, query: query(0) }),
    queryFn: ({ signal }) =>
      readWaiting(
        REQUEST_PAGE,
        async (offset) => {
          const { data } = await sdk.campaignsListCampaignJoinRequests({
            path,
            query: query(offset),
            signal,
            throwOnError: true,
          });
          return extractList<unknown>(data);
        },
        isPersonRequest,
      ),
  };
}

/** Other organizations' campaigns asking to become this campaign's layers, those waiting first. */
export function campaignRequestsOptions(
  campaignId: string,
): UseQueryOptions<P2pLayerRequestOut[], Error> {
  const path = { fundraiser_id: campaignId };
  const query = (offset: number) => ({
    'page[limit]': CAMPAIGN_REQUEST_PAGE,
    'page[offset]': offset,
  });
  return {
    queryKey: campaignsListCampaignLayerRequestsQueryKey({ path, query: query(0) }),
    queryFn: ({ signal }) =>
      readWaiting(
        CAMPAIGN_REQUEST_PAGE,
        async (offset) => {
          const { data } = await sdk.campaignsListCampaignLayerRequests({
            path,
            query: query(offset),
            signal,
            throwOnError: true,
          });
          return extractList<unknown>(data);
        },
        isCampaignRequest,
      ),
  };
}

/** This campaign's own request to join another: the one waiting, else the latest answered in 14 days. */
export function ownLayerRequestOptions(
  campaignId: string,
): UseQueryOptions<P2pOwnLayerRequestOut | null, Error> {
  const options = { path: { fundraiser_id: campaignId } };
  return {
    queryKey: campaignsGetOwnCampaignLayerRequestQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.campaignsGetOwnCampaignLayerRequest({
        ...options,
        signal,
        throwOnError: true,
      });
      const request: unknown = isRecord(data) ? data.request : null;
      return isRecord(request) &&
        typeof request.id === 'string' &&
        typeof request.parent_campaign_id === 'string' &&
        typeof request.status === 'string'
        ? (request as P2pOwnLayerRequestOut)
        : null;
    },
  };
}

/** What this campaign may do about joining the campaign a pasted link names, before it acts. */
export function joinParentPreviewOptions(
  campaignId: string,
  parentCampaignId: string,
): UseQueryOptions<P2pLayerJoinPreviewOut, Error> {
  const options = {
    path: { fundraiser_id: campaignId },
    query: { parent_campaign_id: parentCampaignId },
  };
  return {
    queryKey: campaignsPreviewCampaignJoinParentQueryKey(options),
    queryFn: async ({ signal }) => {
      const { data } = await sdk.campaignsPreviewCampaignJoinParent({
        ...options,
        signal,
        throwOnError: true,
      });
      if (
        !isRecord(data) ||
        data.parent_campaign_id !== parentCampaignId ||
        typeof data.layer_join !== 'string' ||
        !isRecord(data.fit) ||
        !isRecord(data.requires)
      )
        throw new Error('The campaign could not be read.');
      return data;
    },
  };
}

/**
 * The request reads, by their generated keys' `_id`. Built on first use, never at import (a host's
 * tests may mock `@givmo/api-client/query` with only the ops they read).
 */
let requestReadIds: Set<string> | null = null;
function requestReads(): Set<string> {
  requestReadIds ??= new Set(
    [
      campaignsListCampaignJoinRequestsQueryKey({ path: { fundraiser_id: '' } }),
      campaignsListCampaignLayerRequestsQueryKey({ path: { fundraiser_id: '' } }),
      campaignsGetOwnCampaignLayerRequestQueryKey({ path: { fundraiser_id: '' } }),
      campaignsPreviewCampaignJoinParentQueryKey({
        path: { fundraiser_id: '' },
        query: { parent_campaign_id: '' },
      }),
      // The Fundraisers list carries each campaign's waiting count (`pending_requests`).
      orgFundraisersListOrgFundraisersQueryKey({ path: { org_ref: '' } }),
    ].map((key) => key[0]._id),
  );
  return requestReadIds;
}

/**
 * Re-read every request read, the Fundraisers list's counts with them: an answer, an ask or a withdrawal
 * moves a count, a tree and a list in more than one campaign.
 */
export function invalidateRequestReads(client: QueryClient) {
  const ids = requestReads();
  return client.invalidateQueries({
    predicate: (query) => {
      const head = query.queryKey[0];
      return isRecord(head) && typeof head._id === 'string' && ids.has(head._id);
    },
  });
}
